import React from 'react';
import { GatsbyImage } from 'gatsby-plugin-image';
import styled from 'styled-components';
import {
  brandColours,
  breakpointSizes,
  fluidFontSize,
  fontSize,
  minBreakpointQuery,
  sectionPaddings,
  standardColours,
} from '../styles';
import { Container, Tag } from './ui';
import tickIcon from '../images/tick-icon.svg';

const StyledCaseStudyBanner = styled.section`
  ${sectionPaddings('30px', '80px')};
  background-color: ${brandColours.primary};
`;

const StyledInner = styled.div`
  display: grid;
  gap: 40px;

  ${minBreakpointQuery.small`
    gap: 60px;
  `}

  ${minBreakpointQuery.medium`
    grid-template-columns: ${({ $isSalesforceExplainer }) =>
      $isSalesforceExplainer ? '1.5fr 1fr' : 'repeat(2, 1fr)'};
    align-items: center;
  `}

  ${minBreakpointQuery.mlarge`
    gap: 80px;
  `}

  ${minBreakpointQuery.large`
    gap: 110px;
  `}

  ${minBreakpointQuery.xlarge`
    gap: 140px;
  `}

  ${minBreakpointQuery.xxlarge`
    gap: 180px;
  `}

  ${minBreakpointQuery.xxxlarge`
    gap: 220px;
  `}

  ${minBreakpointQuery.xxxxlarge`
    gap: 260px;
  `}
`;

const StyledTop = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledTags = styled.div`
  margin-bottom: 36px;
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
`;

const StyledHeading = styled.h1`
  color: ${brandColours.secondary};
  ${fluidFontSize(
    '24px',
    '40px',
    breakpointSizes.tiny,
    breakpointSizes.xxxxlarge,
  )};
  line-height: 1.2;
`;

const StyledText = styled.p`
  margin-top: 20px;
  line-height: 1.7;

  ${minBreakpointQuery.small`
    margin-top: 25px;
    ${fontSize(18)};
  `}

  ${minBreakpointQuery.large`
    margin-top: 30px;
  `}
`;

const StyledLogo = styled.img`
  margin-top: 20px;
  height: 100%;
  width: 100%;
  max-height: 80px;
  max-width: 200px;

  ${minBreakpointQuery.small`
    margin-top: 25px;
  `}

  ${minBreakpointQuery.large`
    margin-top: 30px;
  `}
`;

const StyledBottom = styled.div`
  display: grid;
`;

const StyledImage = styled(GatsbyImage)`
  border-radius: 20px;
  grid-area: 1/1/1/1;
`;

const StyledResults = styled.div`
  grid-area: 1/1/1/1;
  z-index: 1;
  position: relative;
`;

const StyledResult = styled.div`
  max-width: 300px;
  background-color: ${standardColours.lightestGrey};
  padding: 16px 20px;
  position: absolute;

  &:nth-child(odd) {
    bottom: 40px;
    left: 0;
    border-radius: 0 16px 16px 0;

    ${minBreakpointQuery.medium`
      left: -30px;
      border-radius: 16px;
    `}

    ${minBreakpointQuery.xxlarge`
      left: -40px;
    `}
  }

  &:nth-child(even) {
    bottom: 140px;
    right: 0;
    border-radius: 16px 0 0 16px;

    ${minBreakpointQuery.medium`
      right: -30px;
      border-radius: 16px;
    `}

    ${minBreakpointQuery.xxlarge`
      right: -60px;
    `}
  }
`;

const StyledResultOverline = styled.span`
  display: flex;
  gap: 10px;
  color: ${brandColours.quaternary};
  text-transform: uppercase;
  align-items: center;

  ${fontSize(12)};

  ${minBreakpointQuery.small`
    ${fontSize(14)};
  `}

  &:before {
    content: '';
    background: url(${tickIcon}) no-repeat;
    display: inline-block;
    width: 22px;
    height: 22px;
  }
`;

const StyledResultText = styled.p`
  margin-top: 5px;
`;

const CaseStudyBanner = ({
  categories,
  heading,
  text,
  results,
  logo,
  image,
  isSalesforceExplainer,
}) => (
  <StyledCaseStudyBanner>
    <Container>
      <StyledInner $isSalesforceExplainer={isSalesforceExplainer}>
        <StyledTop>
          {categories && (
            <StyledTags>
              {categories.map((category, id) => (
                <Tag key={id}>{category.name}</Tag>
              ))}
            </StyledTags>
          )}
          <StyledHeading>{heading}</StyledHeading>
          <StyledText>{text}</StyledText>
          {logo && <StyledLogo src={logo.url} alt={logo.alt} loading="eager" />}
        </StyledTop>
        {image && (
          <StyledBottom>
            <StyledImage
              image={image.gatsbyImageData}
              alt={image.alt}
              loading="eager"
            />
            {results && results.length > 0 && (
              <StyledResults>
                {results.map(({ text }, id) => (
                  <StyledResult key={id}>
                    <StyledResultOverline>Result</StyledResultOverline>
                    <StyledResultText>{text}</StyledResultText>
                  </StyledResult>
                ))}
              </StyledResults>
            )}
          </StyledBottom>
        )}
      </StyledInner>
    </Container>
  </StyledCaseStudyBanner>
);

export default CaseStudyBanner;
